// Copyright 2024 Sorama B.V.

import { autoinject } from "aurelia-dependency-injection";
import { Disposable } from "aurelia-framework";
import { AnalysisWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { IModal } from "libraries/WorkflowFramework/Interfaces/IModal";
import { IWorkflowModuleModalInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import {
    IWorkflowModuleConfig,
    WorkflowModuleTypes,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { IWorkflowModuleController } from "libraries/WorkflowFramework/Interfaces/IWorkflowRouter";
import {
    PsychoAcousticAnalysisResultPerMeasurement,
    PsychoAcousticMetricType,
} from "modules/workflows/Analysis/PsychoAcoustics/SpiderChart/SpiderChart";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";
import tippy from "tippy.js";

interface AnalysisGroup {
    analysisTypes: IWorkflowModuleConfig[];
}

/**
 * A class representing the modal dialog for PsychoAcoustic Analysis.
 * This modal allows the user to select a measurement and an associated analysis type.
 * The modal is used to group and manage various types of psychoacoustic analysis operations.
 * @class PsychoAnalysisModal
 * @implements {IModal<PsychoAnalysisModalState, PsychoAnalysisModalReturnState>}
 */
@autoinject
export class PsychoAnalysisModal implements IModal<PsychoAnalysisModalState, PsychoAnalysisModalReturnState> {
    private state: PsychoAnalysisModalState;
    private returnState: IWorkflowModuleConfig;

    private frequencySubscription: Disposable;
    private analysisGroups: AnalysisGroup[] = [];

    // Modal state
    private modalInstance: IWorkflowModuleModalInstance<PsychoAnalysisModalState, PsychoAnalysisModalReturnState>;
    private measurements: GenericMeasurement[] = [];
    private modules: IWorkflowModuleController[] = [];

    private selectedMeasurementIds: string[] = [];
    private observer: MutationObserver;

    /**
     * Creates an instance of PsychoAnalysisModal.
     * @param {IMeasurementProcessorResolver} processorResolver - The service used to resolve measurement processors.
     */
    constructor(private readonly processorResolver: IMeasurementProcessorResolver) {}

    /**
     * Lifecycle hook when the modal is attached to the DOM.
     * Sets up event listeners and initializes measurement data if available.
     */
    attached() {
        this.observeDOMChanges();
    }

    /**
     * Lifecycle hook when the modal is detached from the DOM.
     * Disposes of any active subscriptions.
     */
    detached() {
        if (this.frequencySubscription) {
            this.frequencySubscription.dispose();
        }
        this.observer.disconnect();
    }

    /**
     * Activates the modal, setting up the initial state and loading analysis types.
     *
     * @param {PsychoAnalysisModalState} state - The initial state for the modal, containing measurement data.
     * @param {IWorkflowModuleModalInstance<PsychoAnalysisModalState, PsychoAnalysisModalReturnState>} modalInstance
     * The modal instance to interact with the parent workflow.
     *
     * @returns {Promise<void>} A promise indicating the completion of the modal activation.
     */
    public async activateModal(
        state: PsychoAnalysisModalState,
        modalInstance: IWorkflowModuleModalInstance<PsychoAnalysisModalState, PsychoAnalysisModalReturnState>
    ): Promise<void> {
        this.state = state;
        this.modalInstance = modalInstance;
        this.measurements = this.state.measurements;
        this.selectedMeasurementIds = this.state.selectedMeasurementIds;

        const analysisTypes = await this.getAnalysisTypes();

        // Set all analysis types disabled if there is no measurement
        if (!this.selectedMeasurementIds.length) {
            for (const analysisType of analysisTypes) {
                analysisType.isDisabled = true;
                analysisType.disabledReason = "Select a measurement first";
            }
        }

        // Group the analysis types by type
        for (const type of WorkflowModuleTypes.PsychoAcousticAnalysis) {
            this.analysisGroups.push({ analysisTypes: analysisTypes.filter((x) => x.moduleType == type) });
        }
    }

    /**
     * Closes the modal and returns the selected analysis type.
     * @returns {PsychoAnalysisModalReturnState} The state containing the selected analysis type.
     */
    public close(): PsychoAnalysisModalReturnState {
        return <PsychoAnalysisModalReturnState>{
            selectedAnalysisTypeId: this.returnState,
        };
    }

    /**
     * Selects a workflow module for analysis and adds it to the current workflow.
     * @param {IWorkflowModuleConfig} config - The configuration of the analysis module to select.
     */
    public select(config: IWorkflowModuleConfig): void {
        // If disabled, do nothing
        if (config.isDisabled) {
            return;
        }

        this.modalInstance.parentModule.getWorkflow().addModule(config, null, this.state.metric);
    }

    /**
     * Starts observing DOM changes to link tooltips for analysis types.
     */
    private observeDOMChanges(): void {
        this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" || mutation.type === "attributes") {
                    this.linkTooltips();
                }
            });
        });

        // Observe any tooltips that are added to this modal
        const targetNode = document.getElementById("psycho-analysis-type-modal");
        this.observer.observe(targetNode, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["tooltip"],
        });
    }

    /**
     * Links tooltips to DOM elements with the 'tooltip' attribute.
     * Uses tippy.js to initialize tooltips.
     */
    private linkTooltips(): void {
        const elements = document.querySelectorAll("[tooltip]");

        tippy(elements, {
            content(reference) {
                return reference.getAttribute("tooltip");
            },
            theme: "sorama",
        });
    }

    /**
     * Fetches available analysis types based on the current measurements and processing capabilities.
     * @returns {Promise<IWorkflowModuleConfig[]>} A promise that resolves with a list of available analysis types.
     */
    private async getAnalysisTypes(): Promise<IWorkflowModuleConfig[]> {
        const modulesConfigs: IWorkflowModuleConfig[] = [];

        const canProcessGenericAnalysis = this.processorResolver
            ? await (
                  await this.processorResolver.canProcess(this.measurements, ISpectralProcessingService)
              ).every((x) => x.available)
            : true;

        modulesConfigs.push(
            this.setAnalysisTypeState(
                AnalysisWorkflowModuleCatalog.TimeModule,
                canProcessGenericAnalysis,
                "Cannot process the measurement"
            )
        );
        modulesConfigs.push(
            this.setAnalysisTypeState(
                AnalysisWorkflowModuleCatalog.SpectrumModule,
                canProcessGenericAnalysis,
                "Cannot process the measurement"
            )
        );

        if (this.measurements.length <= 1) {
            // Also check modules which cannot compare measurements
            modulesConfigs.push(
                this.setAnalysisTypeState(
                    AnalysisWorkflowModuleCatalog.SpectrogramModule,
                    canProcessGenericAnalysis,
                    "Cannot process the measurement"
                )
            );
        }

        return modulesConfigs;
    }

    /**
     * Set the state of an analysis type
     * @param analysisType the analysis type to set
     * @param enabled  if the analysis should be enabled
     * @param disabledReason the reason why the analysis type is disabled. Only used if enabled is false
     */
    private setAnalysisTypeState(
        analysisType: IWorkflowModuleConfig,
        enabled = false,
        disabledReason?: string
    ): IWorkflowModuleConfig {
        analysisType.disabledReason = enabled ? null : disabledReason;
        analysisType.isDisabled = !enabled;
        return analysisType;
    }
}

export class PsychoAnalysisModalState {
    public measurements: GenericMeasurement[];

    public psychoAcousticAnalysisResults: PsychoAcousticAnalysisResultPerMeasurement;

    public selectedMeasurementIds: string[];

    public metric: PsychoAcousticMetricType;
}

export class PsychoAnalysisModalReturnState {
    public selectedAnalysisTypeId: IWorkflowModuleConfig;
}
